import React, {useEffect, useState} from 'react';
import {useTheme} from "../ProjectThemeProvider";
import {useToast} from "../fusion-ui/provider/ToastProvider";
import {getArrIndexValue, getDefStr, getJsonValue, isBoolTrue, parseInteger, scrollToTop} from "../fusion-ui/utils/ReactUtils";
import {Label, Select} from "../fusion-ui";
import Pagination from "@mui/material/Pagination";
import {CssVariant} from "../fusion-ui/config/FusionConfig";
import useScreenSize from "../fusion-ui/hook/useScreenSize";
import {printError, printLog} from "../CommonUtils";
import {FramerAnimator} from "../fusion-ui/animation/FramerAnimator";

/**
 * Create a pagination for records.
 *
 * @example
 * <RecordsPagination
 *        id={TAG}
 *        wrapperClassName={""}
 *        className={""}
 *        bgColor={theme.primaryBlue10}
 *        style={{}}
 *        wrapperStyle={{}}
 *        show={true}
 *        scrollToTopOnPageChange={true}
 *        totalRecordsToRender={getArrLen(getRecordListToRender())}
 *        totalRecords={getArrLen(getRecordList())}
 *        recordLimits={PAGINATION_RECORD_LIMITS}
 *        onPageChange={(e, newPageIndex) => {
 *            console.log('RecordsPagination.onLimitChange.newPageIndex:', newPageIndex);
 *            setCurrPageIndex(newPageIndex);
 *        }}
 *        onLimitChange={(newLimit, newPageIndex) => {
 *            console.log('RecordsPagination.onLimitChange.newLimit:', newLimit, 'newPageIndex:', newPageIndex);
 *            setCurrPageRecordLimit(newLimit);
 *            setCurrPageIndex(newPageIndex);
 *        }}
 * />
 */
const RecordsPagination = React.memo(({
                                          id,
                                          wrapperClassName = "",
                                          className = "",
                                          show = true,
                                          bgColor,
                                          style = {},
                                          wrapperStyle = {},
                                          totalRecordsToRender = 0,
                                          totalRecords = 0,
                                          recordLimits = [3, 6, 9, 12, 15, 18],
                                          scrollToTopOnPageChange = true,
                                          onPageChange,
                                          onLimitChange,
                                          currPage = -1,
                                          currPageRecordLimit = -1,
                                          zIndex,
                                          position,
                                          bottom,
                                          top,
                                          left,
                                          right,
                                          ...rest
                                      }) => {
    const TAG = "RecordsPagination";

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isScreenUpSm} = useScreenSize();

    const DEF_PGNTN_RECORD_LIMIT = getArrIndexValue(recordLimits, 0, 2);
    const DEF_PGNTN_START_INDEX = 1;
    const animator = new FramerAnimator();

    const Fields = {
        currPageIndex: 'currPageIndex',
        currPageRecordLimit: 'currPageRecordLimit',
    };
    const [fieldsData, setFieldsData] = useState({
        [Fields.currPageIndex]: DEF_PGNTN_START_INDEX,
        [Fields.currPageRecordLimit]: DEF_PGNTN_RECORD_LIMIT,
    });

    useEffect(() => {
        if (parseInteger(currPage) > 0) updateField(Fields.currPageIndex, currPage);
        if (parseInteger(currPageRecordLimit) > 0) updateField(Fields.currPageRecordLimit, currPageRecordLimit);
    }, [currPage, currPageRecordLimit]);

    return (<>
            {/*{animator.setAnimationType(AnimationType.SLIDE_UP).setDuration(200).render(*/}
            {
                (isBoolTrue(show) && parseInteger(totalRecords) > 0) &&
                <div
                    className={`px-1 py-1 my-1 rounded-3 w-100 d-flex fd-row flex-nowrap flex-wrap-sm jc-between jc-center-sm al-center al-start-sm position-${position} ${wrapperClassName}`}
                    style={{
                        backgroundColor: isScreenUpSm ? 'transparent' : getDefStr(bgColor, theme.primaryBlue10),
                        top: top,
                        bottom: bottom,
                        left: left,
                        right: right,
                        zIndex: zIndex,
                        ...wrapperStyle
                    }}>
                    <Label
                        className={`m-0 p-0 px-2 text-nowrap fs-sm-md select-none`}
                        color={theme.text}>
                        <span className={'fw-semi-bold'}>Total Records:{" "} </span>
                        {/*{`${totalRecordsToRender}/${totalRecords}`}*/}
                        {`${getRecordCountTillNow()}/${totalRecords}`}
                    </Label>

                    <Pagination
                        count={Math.ceil(totalRecords / getField(Fields.currPageRecordLimit))}
                        variant="outlined"
                        shape="rounded"
                        className={"d-flex al-center flex-nowrap fd-row px-2"}
                        size="small"
                        selected={true}
                        page={getField(Fields.currPageIndex)}
                        siblingCount={1}
                        boundaryCount={1}
                        onChange={(e, newPageIndex) => {
                            if (onPageChange) onPageChange(e, newPageIndex);
                            updateField(Fields.currPageIndex, newPageIndex);
                            if (isBoolTrue(scrollToTopOnPageChange)) scrollToTop();
                        }}
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: theme.primaryBlue,
                                borderColor: theme.primaryBlue20,
                                backgroundColor: theme.bg,
                                padding: '.1rem .1rem',
                                maxWidth: '12px',
                                height: '22px',
                                fontSize: 'var(--fs-sm)',
                            }, '& .Mui-selected': {
                                backgroundColor: `${theme.primaryBlue} !important`, color: theme.textWhite, '&:hover': {
                                    backgroundColor: theme.primaryBlue,
                                },
                            },
                        }}
                        {...rest}
                    />
                    <Select
                        id={TAG + id + 'select'}
                        wrapperClassName={`w-auto px-2 me-3`}
                        alignAt={'center'}
                        variant={'theme'}
                        className={``}
                        wrap={`nowrap`}
                        justifyAt={'start'}
                        labelClassName={`px-2 fw-semi-bold pt-1  fs-sm-md  select-none`}
                        label={'Per Page'}
                        // label={'Record Limit'}
                        placeholder={''}
                        options={recordLimits}
                        defOption={getField(Fields.currPageRecordLimit)}
                        onChange={(e, text, value) => {
                            handleRecordVisLimitChange(value);
                        }}
                    />
                </div>
                // )}
            }
        </>
    )

    function getRecordCountTillNow() {
        // totalRecordsToRender
        let count = parseInteger(getField(Fields.currPageIndex)) * parseInteger(getField(Fields.currPageRecordLimit));
        return count > parseInteger(totalRecords) ? totalRecords : count;
    }

    function handleRecordVisLimitChange(newLimit) {
        const fun = 'handleRecordVisLimitChange:';
        try {
            updateField(Fields.currPageRecordLimit, newLimit);

            // If the current page becomes invalid after changing the record display limit, set it to the last available page
            const newPageIndex = Math.ceil(totalRecords / newLimit);
            // const newPageIndex = Math.ceil(records.length / newLimit);
            if (getField(Fields.currPageIndex) > newPageIndex) {
                updateField(Fields.currPageIndex, newPageIndex);
            }

            if (onLimitChange) onLimitChange(newLimit, newPageIndex);
        } catch (error) {
            toast('Selection failed, please try again', CssVariant.danger);
            logErr(fun, error);
        }
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default RecordsPagination;
